import React, { useState } from 'react';
import { 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  Box, 
  Typography, 
  Divider,
  Button,
  Avatar,
  Tooltip,
  Link,
} from '@mui/material';
import { Plus, FileText, FormInput, Calendar } from 'lucide-react';
import { TimelineEvent } from '../../types';
import { TimelineNoteDialog } from './TimelineNoteDialog';
import { users } from '../../data/sampleData';

interface TimelineProps {
  events: TimelineEvent[];
  onAddNote?: (note: Omit<TimelineEvent, 'id'>) => void;
  onFormView?: (taskId: string) => void;
  onDocumentView?: (documentId: string) => void;
  onCalendarView?: (date: string) => void;
}

export const Timeline: React.FC<TimelineProps> = ({ 
  events, 
  onAddNote,
  onFormView,
  onDocumentView,
  onCalendarView,
}) => {
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);

  const sortedEvents = [...events].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const getEventColor = (type: TimelineEvent['type']) => {
    switch (type) {
      case 'court-date':
        return 'primary.main';
      case 'document-filed':
        return 'info.main';
      case 'client-meeting':
        return 'success.main';
      case 'stage-change':
        return 'warning.main';
      case 'form-submission':
        return 'secondary.main';
      default:
        return 'text.primary';
    }
  };

  const getEventUser = (userId: string) => {
    return users.find(user => user.id === userId);
  };

  const renderEventActions = (event: TimelineEvent) => {
    if (event.type === 'form-submission' && event.taskId && onFormView) {
      return (
        <Button
          startIcon={<FormInput size={16} />}
          size="small"
          onClick={() => onFormView(event.taskId!)}
          sx={{ mt: 1 }}
        >
          View Form
        </Button>
      );
    }

    if (event.type === 'document-filed' && event.documentId && onDocumentView) {
      return (
        <Button
          startIcon={<FileText size={16} />}
          size="small"
          onClick={() => onDocumentView(event.documentId!)}
          sx={{ mt: 1 }}
        >
          View Document
        </Button>
      );
    }

    if (event.type === 'custom' && event.metadata?.eventType && onCalendarView) {
      return (
        <Button
          startIcon={<Calendar size={16} />}
          size="small"
          onClick={() => onCalendarView(event.date)}
          sx={{ mt: 1 }}
        >
          View in Calendar
        </Button>
      );
    }

    return null;
  };

  return (
    <>
      <Paper>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Timeline</Typography>
          <Button 
            startIcon={<Plus size={18} />}
            size="small"
            onClick={() => setNoteDialogOpen(true)}
          >
            Add Note
          </Button>
        </Box>
        <Divider />
        <List>
          {sortedEvents.map((event, index) => {
            const user = getEventUser(event.addedBy);
            
            return (
              <React.Fragment key={event.id}>
                <ListItem
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    py: 2,
                  }}
                >
                  {user && (
                    <Tooltip title={`${user.name} (${user.role})`} arrow placement="left">
                      <Avatar
                        src={user.avatar}
                        alt={user.name}
                        sx={{ 
                          width: 32, 
                          height: 32,
                          mt: 0.5,
                        }}
                      />
                    </Tooltip>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography 
                        component="span" 
                        sx={{ 
                          color: getEventColor(event.type),
                          fontWeight: 500,
                        }}
                      >
                        {event.description}
                      </Typography>
                      {event.metadata?.eventType && (
                        <Typography
                          variant="caption"
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            textTransform: 'capitalize',
                          }}
                        >
                          {event.metadata.eventType}
                        </Typography>
                      )}
                    </Box>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        component="time"
                        variant="caption"
                        sx={{ 
                          color: 'text.secondary',
                        }}
                      >
                        {new Date(event.date).toLocaleString()}
                      </Typography>
                      {user && (
                        <>
                          <Typography
                            variant="caption"
                            sx={{ 
                              color: 'text.secondary',
                            }}
                          >
                            •
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ 
                              color: 'text.secondary',
                              fontWeight: 500,
                            }}
                          >
                            {user.name}
                          </Typography>
                        </>
                      )}
                    </Box>
                    {renderEventActions(event)}
                  </Box>
                </ListItem>
                {index < events.length - 1 && <Divider component="li" />}
              </React.Fragment>
            );
          })}
        </List>
      </Paper>

      <TimelineNoteDialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        onSave={onAddNote || (() => {})}
      />
    </>
  );
};